import {
  Box,
  Columns,
  Container,
  Divider,
  Heading,
  Stack,
  Text,
  UnstyledLink,
} from '@spaceship-fspl/components';
import { formatDate } from '@spaceship-fspl/helpers';
import { FeatherDownloadIcon } from '@spaceship-fspl/icons-web';
import {
  backgroundColor,
  borderRadius,
  color,
  marginRight,
  marginTop,
  paddingLeft,
  paddingX,
  paddingY,
} from '@spaceship-fspl/styles';
import {
  Article,
  ArticleContentContainer,
  getContentPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  Metadata,
  MetadataProps,
  withContentPageLayout,
} from 'components';
import {
  getNewsArticle,
  GetNewsArticleResponse,
  getNewsArticleSlugs,
  mapSlicesToProps,
  RichText,
} from 'data/prismic';
import { GetStaticPaths, NextPage } from 'next';
import { Link } from 'prismic-reactjs';
import { ParsedUrlQuery } from 'querystring';
import styled from 'styled-components';

const DownloadText = styled(Text).attrs({
  variant: 1,
  color: 'neutral.100',
})<{ variant?: number; color?: string }>`
  ${marginRight('sm')}
`;

const DownloadLink = styled(UnstyledLink)`
  ${backgroundColor({ xs: 'neutral.030', md: 'neutral.000' })}
  ${paddingX({ xs: 'md', md: 'none' })}
  ${paddingY({ xs: 'xxs', md: 'none' })}
  ${borderRadius('md')}
  ${color('neutral.100')}
  align-items: center;
  display: inline-flex;
  transform: 0.15s;

  &:hover {
    &,
    ${DownloadText} {
      ${color('indigo.070')}
    }
  }
`;

const Footnotes = styled(Text).attrs({
  variant: 4,
  color: 'neutral.080',
  component: 'div',
})<{ variant?: number; color?: string; component?: string }>`
  margin: 0;
  ${marginTop('xl')}

  ol,
  ul {
    ${paddingLeft('sm')}
  }
`;

interface NewsArticleProps extends Omit<GetNewsArticleResponse, 'body'> {
  metadata?: MetadataProps | null;
}

interface NewsArticleContext extends ParsedUrlQuery {
  slug: string;
}

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await getNewsArticleSlugs();

  return {
    paths: slugs.map((slug) => ({
      params: { slug },
    })),
    fallback: false,
  };
};

export const getStaticProps = getContentPageLayoutStaticProps<
  NewsArticleProps,
  NewsArticleContext
>(async (context) => {
  const articleSlug = context?.params?.slug;
  const document = await getNewsArticle(articleSlug || '');
  const data = mapSlicesToProps(document.body, {
    News_articleBodyMetadata: (slice) =>
      getMetadataPropsFromPrismicSlice(slice),
  });

  return {
    props: {
      metadata: data.News_articleBodyMetadata,
      title: document.title,
      published_date: document.published_date,
      pdf: document.pdf,
      content: document.content,
      footnotes: document.footnotes,
    },
  };
});

const NewsArticle: NextPage<NewsArticleProps> = ({
  metadata,
  title,
  published_date: publishedDate,
  pdf,
  content,
  footnotes,
}) => (
  <>
    {metadata && <Metadata {...metadata} />}

    <Box
      marginTop={{ xs: 'lg', lg: 'xl' }}
      marginBottom={{ xs: 'xl', md: 'xxl', lg: 'xxxl' }}
    >
      <Container>
        <Columns alignX="center" spaceY={{ xs: 'xl', lg: 'xxl' }}>
          <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 8 / 12 }}>
            <Stack spaceY={{ xs: 'sm', md: 'md', lg: 'lg' }}>
              <Heading
                variant={{ xs: 2, md: 1 }}
                align={{ xs: 'center', md: 'left' }}
                component="h1"
              >
                {title}
              </Heading>

              <Columns
                alignY="center"
                alignX={{ xs: 'center', md: 'left' }}
                spaceY="sm"
              >
                <Columns.Column width={{ xs: 1, md: 'max' }}>
                  {publishedDate && (
                    <Text
                      variant={1}
                      color="neutral.080"
                      align={{ xs: 'center', md: 'left' }}
                    >
                      {formatDate(new Date(publishedDate), 'dd MMMM yyyy')}
                    </Text>
                  )}
                </Columns.Column>

                {pdf && (
                  <Columns.Column width={{ xs: 1, md: 'min' }}>
                    <Box
                      display="flex"
                      justifyContent={{ xs: 'center', md: 'right' }}
                    >
                      <DownloadLink href={Link.url(pdf)} target="_blank">
                        <DownloadText>Download PDF</DownloadText>
                        <FeatherDownloadIcon />
                      </DownloadLink>
                    </Box>
                  </Columns.Column>
                )}
              </Columns>
            </Stack>
          </Columns.Column>

          <Columns.Column width={1}>
            <Divider color="neutral.050" />
          </Columns.Column>

          <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 8 / 12 }}>
            <ArticleContentContainer>
              <Article>
                <RichText render={content} />
              </Article>
            </ArticleContentContainer>

            {footnotes && (
              <Footnotes>
                <RichText render={footnotes} />
              </Footnotes>
            )}
          </Columns.Column>
        </Columns>
      </Container>
    </Box>
  </>
);

export default withContentPageLayout()(NewsArticle);
